import { mapKeys, mapValues, pick } from 'lodash'
import { withDependencies, named } from '@wix/thunderbolt-ioc'
import {
	Fetch,
	IFetchApi,
	EditorFeatureConfigSymbol,
	ILogger,
	LoggerSymbol,
	SdkHandlersProvider,
	BrowserWindow,
	BrowserWindowSymbol,
	FeatureExportsSymbol,
	isViewerFragmentSymbol,
	ViewerModel,
	ViewerModelSym,
} from '@wix/thunderbolt-symbols'
import type { ISessionManager, SessionHandlers, LoadNewSessionReason, OnLoadSessionCallback } from '../types'
import type { DsSessionModel, SessionManagerEditorConfig } from './types'
import { name, sessionExportsNamespace } from '../symbols'
import { DEFAULT_EXPIRATION_TIME, META_SITE_APP_ID } from '../constants'
import { IFeatureExportsStore } from 'thunderbolt-feature-exports'

const noop = () => undefined

class FetchDynamicModelFailedError extends Error {
	requestId: string
	constructor(message: string, requestId: string = '') {
		super(message)
		this.name = 'FetchDynamicModelFailedError'
		this.requestId = requestId
	}
}

export const SessionManager = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		Fetch,
		LoggerSymbol,
		named(FeatureExportsSymbol, name),
		ViewerModelSym,
		isViewerFragmentSymbol,
	],
	(
		editorFeatureConfig: SessionManagerEditorConfig,
		browserWindow: BrowserWindow,
		fetchApi: IFetchApi,
		logger: ILogger,
		sessionExports: IFeatureExportsStore<typeof sessionExportsNamespace>,
		{ mode: { debug } }: ViewerModel,
		isViewerFragment: boolean
	): ISessionManager & SdkHandlersProvider<SessionHandlers> => {
		let sessionTimeoutPointer: number

		const onLoadSessionCallbacks: Set<OnLoadSessionCallback> = new Set()

		const addLoadNewSessionCallback = (callback: OnLoadSessionCallback) => {
			onLoadSessionCallbacks.add(callback)
			return () => onLoadSessionCallbacks.delete(callback)
		}

		const sessionModelState = editorFeatureConfig.sessionModel
		const getSessionModel = (): DsSessionModel =>
			Object.keys(sessionModelState).length > 0 ? sessionModelState : editorFeatureConfig.sessionModel

		const getMetaSiteInstance = () => {
			const metaSiteAppDefinitionId = editorFeatureConfig.clientSpecMap[META_SITE_APP_ID]
			const metaSiteApp = getSessionModel()[metaSiteAppDefinitionId]
			return (metaSiteApp && metaSiteApp.instance) || editorFeatureConfig.metaSiteInstance
		}

		const fetchDynamicModel = async () => {
			let res
			try {
				res = await fetchApi.envFetch(editorFeatureConfig.dynamicModelApiUrl, {
					credentials: 'same-origin',
					headers: { authorization: getMetaSiteInstance() },
				})
			} catch (err) {
				throw new FetchDynamicModelFailedError('Network error: ' + err.message)
			}
			const requestId = res.headers?.get('X-Wix-Request-Id') || ''
			if (res.status < 200 || res.status >= 300) {
				throw new FetchDynamicModelFailedError(`Fetch failed with HTTP status ${res.status}`, requestId)
			}
			let data
			try {
				data = await res.json()
			} catch (ex) {
				throw new FetchDynamicModelFailedError('Non-json response', requestId)
			}
			return mapValues(mapKeys(data, 'appDefinitionId'), (clientSpec) => pick(clientSpec, 'instance'))
		}

		const invokeSessionLoadCallbacks = (reason: LoadNewSessionReason) => {
			const instances = mapValues(getSessionModel(), 'instance')
			onLoadSessionCallbacks.forEach((callback) =>
				callback({
					results: {
						instances,
					},
					reason,
				})
			)
		}

		const loadNewSession: ISessionManager['loadNewSession'] = async (
			{ reason } = { reason: 'noSpecificReason' }
		) => {
			if (isViewerFragment) {
				if (debug) {
					console.error('loadNewSession is not implemented in minisites')
				}
				return
			}

			try {
				const newSession = await fetchDynamicModel()
				Object.assign(sessionModelState, newSession)
				invokeSessionLoadCallbacks(reason)
			} catch (error) {
				logger.captureError(error, {
					tags: { feature: 'session-manager', fetchFail: 'dynamicModel' },
					extra: {
						rawError: JSON.stringify(error),
						stack: error.stack,
					},
				})
			}

			setSessionTimeout()
		}

		const setSessionTimeout = () => {
			if (sessionTimeoutPointer) {
				browserWindow!.clearTimeout(sessionTimeoutPointer)
			}

			sessionTimeoutPointer = browserWindow!.setTimeout(
				() => loadNewSession({ reason: 'expiry' }),
				DEFAULT_EXPIRATION_TIME
			)
		}

		const getAppInstanceByAppDefId = (appDefId: string) => {
			const app = getSessionModel()[appDefId] || {}
			return app.instance
		}
		const getVisitorId = noop
		sessionExports.export({ getVisitorId, getAppInstanceByAppDefId })

		if (!isViewerFragment) {
			// set initial timeout for refresh
			setSessionTimeout()
		}

		return {
			getAllInstances() {
				return getSessionModel()
			},
			getAppInstanceByAppDefId,
			getSiteMemberId: noop,
			getSmToken: noop,
			getVisitorId,
			loadNewSession,
			addLoadNewSessionCallback,
			getHubSecurityToken() {
				return 'NO_HS'
			},
			getUserSession() {
				return ''
			},
			getCtToken: noop,
			setUserSession() {},
			getSdkHandlers: () => ({
				getMediaAuthToken: () => Promise.resolve(editorFeatureConfig.mediaAuthToken || undefined),
				loadNewSession,
				addLoadNewSessionCallback: async (callback) => addLoadNewSessionCallback(callback),
			}),
		}
	}
)
